import React, { useEffect, useRef, useState } from 'react';
import logoSura from '../logo_sura_blanco.png';
import logoAnsa from './ansa-banner-assets/ansa-blanco.png';
import logoVale from './venta-especial-assets/marcas/vale.png';
import logoTisuf from './venta-especial-assets/marcas/tisuf.png';

// Campaña para que el personal vote por los productos SURA en los Premios ANSA
// 2026 (Marca Preferida del Año). Vale compite en Detergente; Tisuf no está
// nominado en Papel Higiénico, así que se vota marcando "Otro" y escribiendo
// Tisuf. Calca la gráfica oficial de ANSA: fondo azul noche casi negro con un
// haz de luz y brillitos, letras de plata biselada y franjas azules con puntos.
// A la derecha, una boleta se llena sola: marca Vale, marca Otro, escribe Tisuf
// y se sella. La historia dura 7 s, el final se queda 7 s y vuelve a empezar; el
// texto y la boleta no se repiten. Todo el banner abre el formulario en otra
// pestaña.

// Desde cuándo sale ('AAAA-MM-DD') y cuántos días
const DESDE = '2026-10-28';
const DIAS_VISIBLE = 14;
const ENLACE_VOTO = 'https://docs.google.com/forms/d/1zu4U0xmDMGLghYT71I773DOPuKkfXrMJV3etBfbGxGc/viewform?edit_requested=true';

const EN_DESARROLLO = process.env.NODE_ENV === 'development';
const PERIODO = 14; // 7 s de historia + 7 s con el final quieto

function enFechas() {
  if (EN_DESARROLLO) return true;
  if (!DESDE) return false;
  const inicio = new Date(`${DESDE}T00:00:00`);
  const fin = new Date(inicio.getTime() + DIAS_VISIBLE * 86400000);
  const ahora = new Date();
  return ahora >= inicio && ahora < fin;
}

// ── Paleta: la del afiche de ANSA ──
const NOCHE = '#030918';
const MARINO = '#0B1150';
const REAL = '#1C2BA8';
const INDIGO = '#131463';
const MARCA = '#3B5BDB';
const PAPEL = '#F4F6FB';
const TINTA = '#0B2A6B';
const GRIS = '#DCE2EE';

// ── Línea de tiempo ──
// Cada pista va en segundos sobre el ciclo de 14 s; se convierte a keyframes
// en %. La última parada de cada pista es también el estado final, que se
// sostiene hasta el final del ciclo.

const EO = 'cubic-bezier(.22,1,.36,1)';
const REBOTE = 'cubic-bezier(.3,1.6,.6,1)';

const oculto = (desplazamiento) => `opacity:0;transform:translateY(${desplazamiento}px)`;
const visible = 'opacity:1;transform:translateY(0)';
const aparecer = (t, desplazamiento = 8) => [
  [0, oculto(desplazamiento)],
  [t, oculto(desplazamiento), EO],
  [t + 0.5, visible],
];
const brotar = (t) => [
  [0, 'opacity:0;transform:scale(.5)'],
  [t, 'opacity:0;transform:scale(.5)', REBOTE],
  [t + 0.45, 'opacity:1;transform:scale(1)'],
];
const tocar = (t) => [
  [0, 'opacity:0;transform:scale(.2)'],
  [t, 'opacity:0;transform:scale(.2)'],
  [t + 0.02, 'opacity:.5;transform:scale(.2)', 'ease-out'],
  [t + 0.55, 'opacity:0;transform:scale(1.9)'],
];
const trazar = (t, largo, dura = 0.35) => [
  [0, `stroke-dashoffset:${largo}`],
  [t, `stroke-dashoffset:${largo}`, EO],
  [t + dura, 'stroke-dashoffset:0'],
];
const teclear = (t) => [[0, 'fill-opacity:0'], [t, 'fill-opacity:0'], [t + 0.04, 'fill-opacity:1']];

const PISTAS = {
  // Texto
  'pab-firma': aparecer(0.1),
  'pab-titulo-1': aparecer(0.25, 10),
  'pab-titulo-2': aparecer(0.42, 10),
  'pab-bajada': aparecer(0.65),
  'pab-cta': aparecer(0.85),

  // La boleta entra y se queda; el marco de líneas con punta de ANSA la abraza
  'pab-boleta': [
    [0, 'opacity:0;transform:translateY(26px) rotate(2.5deg)'],
    [0.5, 'opacity:0;transform:translateY(26px) rotate(2.5deg)', EO],
    [1.25, 'opacity:1;transform:translateY(0) rotate(0deg)'],
  ],
  'pab-marco-1': trazar(1.0, 200, 0.8),
  'pab-marco-2': trazar(1.2, 200, 0.8),
  'pab-punto-1': brotar(1.7),
  'pab-punto-2': brotar(2.0),

  // Detergente: Vale
  'pab-toque-1': tocar(2.2),
  'pab-marca-1': brotar(2.25),
  'pab-tilde-1': trazar(2.35, 24),
  'pab-vale': brotar(2.75),

  // Papel higiénico: Otro, y se escribe Tisuf
  'pab-toque-2': tocar(3.9),
  'pab-marca-2': brotar(3.95),
  'pab-tilde-2': trazar(4.05, 24),
  'pab-campo': [[0, 'opacity:0'], [4.3, 'opacity:0', EO], [4.6, 'opacity:1']],
  'pab-pista': [[0, 'opacity:0'], [0.5, 'opacity:1'], [4.95, 'opacity:1'], [5.05, 'opacity:0']],
  'pab-cursor-1': [[0, 'opacity:0'], [4.5, 'opacity:0'], [4.52, 'opacity:1'], [4.93, 'opacity:1'], [4.95, 'opacity:0']],
  'pab-l1': teclear(5.0),
  'pab-l2': teclear(5.18),
  'pab-l3': teclear(5.34),
  'pab-l4': teclear(5.5),
  'pab-l5': teclear(5.68),
  'pab-cursor-2': [[0, 'opacity:0'], [5.72, 'opacity:0'], [5.74, 'opacity:1']],
  'pab-tisuf': brotar(6.1),

  // El sello cae sobre la boleta
  'pab-sello': [
    [0, 'opacity:0;transform:scale(1.7) rotate(-28deg)'],
    [6.8, 'opacity:0;transform:scale(1.7) rotate(-28deg)', 'cubic-bezier(.5,0,.75,0)'],
    [7.1, 'opacity:1;transform:scale(1) rotate(0deg)'],
  ],
};

// En el bucle se quedan quietos en su estado final
const PERSISTENTES = new Set([
  'pab-firma', 'pab-titulo-1', 'pab-titulo-2', 'pab-bajada', 'pab-cta',
  'pab-boleta', 'pab-marco-1', 'pab-marco-2', 'pab-punto-1', 'pab-punto-2',
]);
// Lo que deja la historia al terminar: en el bucle se apaga al empezar cada
// vuelta para que el voto vuelva a quedar en blanco
const LLEGADA = new Set([
  'pab-marca-1', 'pab-tilde-1', 'pab-vale', 'pab-marca-2', 'pab-tilde-2', 'pab-campo',
  'pab-l1', 'pab-l2', 'pab-l3', 'pab-l4', 'pab-l5', 'pab-cursor-2', 'pab-tisuf', 'pab-sello',
]);

const pct = (t) => `${+((t / PERIODO) * 100).toFixed(3)}%`;
const keyframes = (nombre, paradas) => {
  const cuadros = paradas
    .map(([t, decl, ease]) => `${pct(t)}{${decl}${ease ? `;animation-timing-function:${ease}` : ''}}`)
    .join('');
  return `@keyframes ${nombre}{${cuadros}100%{${paradas[paradas.length - 1][1]}}}`;
};

const cssPistas = Object.entries(PISTAS).map(([clase, paradas]) => {
  const final = paradas[paradas.length - 1][1];
  const reglas = [
    keyframes(`${clase}-k`, paradas),
    `.pab[data-estado="intro"] .${clase}{animation:${clase}-k ${PERIODO}s linear both}`,
    `.pab[data-estado="final"] .${clase}{${final}}`,
  ];
  if (PERSISTENTES.has(clase)) {
    reglas.push(`.pab[data-estado="bucle"] .${clase}{${final}}`);
  } else if (LLEGADA.has(clase)) {
    const apagado = [[0, final, 'ease-in'], [0.5, paradas[0][1]], ...paradas.slice(1)];
    reglas.push(keyframes(`${clase}-b`, apagado));
    reglas.push(`.pab[data-estado="bucle"] .${clase}{animation:${clase}-b ${PERIODO}s linear infinite both}`);
  } else {
    reglas.push(`.pab[data-estado="bucle"] .${clase}{animation:${clase}-k ${PERIODO}s linear infinite both}`);
  }
  return reglas.join('\n');
}).join('\n');

// Plata biselada como el titular del afiche: cada letra va de blanco a la
// izquierda a gris acero a la derecha, con canto claro. El destello la cruza
// letra por letra.
const PLATA = 'linear-gradient(100deg, #FFFFFF 0%, #F0F2F5 28%, #BDC2CB 60%, #8F95A2 100%)';
const PLATA_SUAVE = 'linear-gradient(100deg, #F2F3F6 0%, #D9DCE2 30%, #A8ADB8 64%, #848A97 100%)';
const DESTELLO = 'linear-gradient(100deg, transparent 38%, rgba(255,255,255,0.95) 50%, transparent 62%)';

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@500;600&display=swap');

.pab-contenedor { container-type: inline-size; }

.pab-enlace {
  display: block;
  border-radius: 1.25rem;
  text-decoration: none;
  color: inherit;
  outline: none;
}
.pab-enlace:focus-visible { box-shadow: 0 0 0 3px #8FB0FF; }

.pab {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  border-radius: 1.25rem;
  aspect-ratio: 1000 / 380;
  color: #FFFFFF;
  background: ${NOCHE};
  box-shadow: 0 18px 40px -28px rgba(3, 9, 24, 0.9);
}

.pab-fondo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
}

.pab-texto {
  position: absolute;
  left: 5%;
  top: 9%;
  width: 48%;
  z-index: 2;
}

/* SURA y ANSA, como firma conjunta */
.pab-firma {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.8cqw, 18px);
  margin-bottom: 1em;
}
.pab-firma img { display: block; height: auto; }
.pab-logo-sura { width: clamp(58px, 8.8cqw, 94px); }
.pab-logo-ansa { width: clamp(62px, 9.4cqw, 100px); }
.pab-separador { width: 1px; align-self: stretch; background: rgba(255, 255, 255, 0.35); }

.pab-titulo {
  margin: 0;
  font-family: 'Montserrat', 'Inter', sans-serif;
  font-size: clamp(20px, 4.3cqw, 44px);
  line-height: 1.04;
  text-transform: uppercase;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.55));
}
.pab-titulo > span { display: block; white-space: nowrap; }
.pab-titulo-1 { font-weight: 500; }
.pab-titulo-2 { font-weight: 700; }

.pab-letra {
  display: inline-block;
  background-image: ${DESTELLO}, ${PLATA};
  background-size: 360% 100%, 100% 100%;
  background-position: 130% 0, 0 0;
  background-repeat: no-repeat;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  -webkit-text-stroke: 0.018em rgba(255, 255, 255, 0.45);
  animation: pab-destello-k 7s linear infinite;
  animation-delay: calc(var(--i) * 40ms + 2.2s);
}
.pab-titulo-1 .pab-letra { background-image: ${DESTELLO}, ${PLATA_SUAVE}; }
@keyframes pab-destello-k {
  0% { background-position: 130% 0, 0 0; }
  9%, 100% { background-position: -30% 0, 0 0; }
}

.pab-bajada {
  margin: 0.9em 0 0;
  max-width: 31em;
  font-size: clamp(11px, 1.45cqw, 15px);
  line-height: 1.45;
  color: #AEB9D3;
  text-wrap: pretty;
}

.pab-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em 1em;
  margin-top: 1.15em;
  font-size: clamp(11px, 1.45cqw, 15px);
}

/* El bloque "Vota ahora" del afiche */
.pab-boton {
  display: inline-flex;
  align-items: center;
  padding: 0.62em 1.35em;
  border-radius: 6px;
  font-family: 'Montserrat', 'Inter', sans-serif;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #FFFFFF;
  background: linear-gradient(135deg, ${REAL} 0%, ${INDIGO} 100%);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16), 0 10px 24px -12px rgba(28, 43, 168, 0.9);
  transition: filter 0.2s ease, transform 0.2s ease;
}
.pab-enlace:hover .pab-boton { filter: brightness(1.25); transform: translateY(-1px); }

.pab-tiempo { color: #7F8BA8; }

.pab-voto {
  position: absolute;
  right: 3.5%;
  top: 50%;
  width: 42%;
  height: auto;
  transform: translateY(-50%);
  z-index: 1;
  overflow: visible;
  pointer-events: none;
}

/* Escalas sobre su propio centro */
.pab-centro { transform-box: fill-box; transform-origin: center; }

.pab-marco-1 { stroke-dasharray: 200; }
.pab-marco-2 { stroke-dasharray: 200; }
.pab-tilde-1, .pab-tilde-2 { stroke-dasharray: 24; }

.pab-titila { animation: pab-titila-k 2.6s ease-in-out infinite; }
@keyframes pab-titila-k { 0%, 100% { opacity: 0.15; } 50% { opacity: 1; } }

.pab-chispa { animation: pab-chispa-k 3.4s ease-in-out infinite; }
@keyframes pab-chispa-k {
  0%, 100% { opacity: 0; transform: scale(.3) rotate(0deg); }
  45% { opacity: 1; transform: scale(1) rotate(20deg); }
  70% { opacity: 0; transform: scale(.5) rotate(35deg); }
}

.pab-parpadeo { animation: pab-parpadeo-k 1s steps(1) infinite; }
@keyframes pab-parpadeo-k { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }

.pab-reloj { position: absolute; width: 0; height: 0; overflow: hidden; }
@keyframes pab-reloj-k { from { opacity: 0; } to { opacity: 1; } }
.pab[data-estado="intro"] .pab-reloj { animation: pab-reloj-k ${PERIODO}s linear both; }

/* Fuera de pantalla o con la pestaña oculta, la historia espera */
.pab[data-pausado="true"] *,
.pab[data-pausado="true"] *::before,
.pab[data-pausado="true"] *::after { animation-play-state: paused !important; }

${cssPistas}

/* En teléfono: texto arriba, la boleta abajo */
@container (max-width: 559px) {
  .pab { aspect-ratio: auto; display: flex; flex-direction: column; padding-bottom: 18px; }
  .pab-texto { position: relative; left: auto; top: auto; width: auto; padding: 22px 22px 6px; }
  .pab-titulo { font-size: clamp(20px, 7.2cqw, 34px); }
  .pab-voto { position: relative; right: auto; top: auto; transform: none; width: min(92%, 420px); margin: 10px auto 0; }
}

@media (prefers-reduced-motion: reduce) {
  .pab-letra, .pab-titila, .pab-chispa, .pab-parpadeo { animation: none; }
  .pab-chispa { opacity: 0.8; }
}
`;

// Letras de plata, una por una (el destello las recorre en orden)
const Plata = ({ texto, desde = 0 }) => (
  <>
    {[...texto].map((c, i) => (
      c === ' '
        ? ' '
        : <span key={i} className="pab-letra" style={{ '--i': desde + i }}>{c}</span>
    ))}
  </>
);

// ── Fondo: azul noche casi negro, haz de luz, brillitos y franjas ──

// Siempre los mismos brillitos: generador pseudoaleatorio con semilla. Más
// densos arriba, como en el afiche.
const BRILLITOS = (() => {
  let s = 2026;
  const azar = () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
  const tonos = ['#FFFFFF', '#C9D8FF', '#8FB0FF', '#5F86E8'];
  const puntos = Array.from({ length: 170 }, (_, i) => {
    const veta = azar() < 0.3;
    return {
      x: azar() * 1000,
      y: Math.pow(azar(), 1.5) * 330,
      r: 0.5 + azar() * 1.4,
      largo: 2.5 + azar() * 4,
      giro: Math.round(azar() * 180),
      color: tonos[Math.floor(azar() * tonos.length)],
      o: 0.25 + azar() * 0.65,
      veta,
      titila: i % 6 === 0,
      retardo: (azar() * 2.6).toFixed(2),
    };
  });
  const chispas = Array.from({ length: 9 }, () => ({
    x: 40 + azar() * 920,
    y: 14 + Math.pow(azar(), 1.3) * 250,
    t: 3 + azar() * 4,
    retardo: (azar() * 3.4).toFixed(2),
  }));
  return { puntos, chispas };
})();

// Destello de cuatro puntas centrado en (0, 0)
const CHISPA = 'M0 -1 C0.12 -0.12 0.12 -0.12 1 0 C0.12 0.12 0.12 0.12 0 1 C-0.12 0.12 -0.12 0.12 -1 0 C-0.12 -0.12 -0.12 -0.12 0 -1 Z';

const Fondo = () => (
  <svg className="pab-fondo" viewBox="0 0 1000 380" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <defs>
      <radialGradient id="pab-velo" cx="28%" cy="-25%" r="80%">
        <stop offset="0" stopColor="#0B2466" stopOpacity="0.85" />
        <stop offset="1" stopColor={NOCHE} stopOpacity="0" />
      </radialGradient>
      <radialGradient id="pab-haz" cx="0.5" cy="0" r="1" gradientTransform="translate(0.5 0) scale(0.32 1.1) translate(-0.5 0)">
        <stop offset="0" stopColor="#2F63F0" stopOpacity="0.55" />
        <stop offset="0.5" stopColor="#1A3FB8" stopOpacity="0.22" />
        <stop offset="1" stopColor="#0B1C66" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="pab-real" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#13299A" />
        <stop offset="1" stopColor="#2447C8" />
      </linearGradient>
      <linearGradient id="pab-plata" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#7E8799" />
        <stop offset="0.45" stopColor="#E7EBF2" />
        <stop offset="1" stopColor="#A9B1C2" />
      </linearGradient>
      <linearGradient id="pab-celeste" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#2D4FC4" />
        <stop offset="1" stopColor="#5B83F0" />
      </linearGradient>
      <pattern id="pab-puntos" width="6" height="6" patternUnits="userSpaceOnUse">
        <circle cx="1.5" cy="1.5" r="0.85" fill="#FFFFFF" opacity="0.4" />
      </pattern>
    </defs>

    <rect width="1000" height="380" fill={NOCHE} />
    <rect width="1000" height="380" fill="url(#pab-velo)" />
    {/* Haz de luz del escenario, detrás del voto */}
    <rect x="560" y="0" width="420" height="380" fill="url(#pab-haz)" />

    {BRILLITOS.puntos.map((p, i) => (p.veta ? (
      <rect
        key={i}
        x={p.x - p.largo / 2}
        y={p.y - 0.5}
        width={p.largo}
        height="1"
        rx="0.5"
        fill={p.color}
        opacity={p.o}
        transform={`rotate(${p.giro} ${p.x} ${p.y})`}
        className={p.titila ? 'pab-titila' : undefined}
        style={p.titila ? { animationDelay: `${p.retardo}s` } : undefined}
      />
    ) : (
      <circle
        key={i}
        cx={p.x}
        cy={p.y}
        r={p.r}
        fill={p.color}
        opacity={p.o}
        className={p.titila ? 'pab-titila' : undefined}
        style={p.titila ? { animationDelay: `${p.retardo}s` } : undefined}
      />
    )))}
    {BRILLITOS.chispas.map((c, i) => (
      <g key={i} transform={`translate(${c.x} ${c.y}) scale(${c.t})`}>
        <path d={CHISPA} fill="#FFFFFF" className="pab-chispa pab-centro" style={{ animationDelay: `${c.retardo}s` }} />
      </g>
    ))}

    {/* Franjas del afiche: marino, azul real con puntos, plata, celeste */}
    <path d="M-20 330 C 300 360, 640 330, 1020 220 L1020 400 L-20 400 Z" fill={MARINO} />
    <path d="M-20 328 C 300 358, 640 328, 1020 218" fill="none" stroke="#DDE3EE" strokeOpacity="0.55" strokeWidth="1.4" />
    <path d="M-20 346 C 300 370, 650 344, 1020 246 L1020 400 L-20 400 Z" fill="url(#pab-real)" />
    <path d="M-20 346 C 300 370, 650 344, 1020 246 L1020 400 L-20 400 Z" fill="url(#pab-puntos)" opacity="0.5" />
    <path d="M-20 356 C 300 378, 655 352, 1020 262 L1020 280 C 660 368, 300 390, -20 370 Z" fill="url(#pab-plata)" />
    <path d="M-20 356 C 300 378, 655 352, 1020 262 L1020 280 C 660 368, 300 390, -20 370 Z" fill="url(#pab-puntos)" />
    <path d="M-20 372 C 300 392, 662 370, 1020 290 L1020 400 L-20 400 Z" fill="url(#pab-celeste)" />
    <path d="M-20 372 C 300 392, 662 370, 1020 290 L1020 400 L-20 400 Z" fill="url(#pab-puntos)" opacity="0.6" />
    <path d="M-20 390 C 320 402, 680 394, 1020 330 L1020 400 L-20 400 Z" fill={MARINO} />
  </svg>
);

// ── Boleta (viewBox 440 x 340) ──

const FUENTE = "'Montserrat', 'Inter', sans-serif";
const CONDENSADA = "'Oswald', 'Montserrat', sans-serif";

const Opcion = ({ x, y, w }) => (
  <g>
    <rect x={x} y={y} width={w} height="30" rx="15" fill="#FFFFFF" stroke={GRIS} strokeWidth="1.5" />
    <circle cx={x + 16} cy={y + 15} r="7" fill="none" stroke="#B8C2D6" strokeWidth="1.5" />
    <rect x={x + 30} y={y + 12} width={w - 44} height="6" rx="3" fill={GRIS} />
  </g>
);

// Opción con nombre, marcada en la historia
const Elegida = ({ x, y, w, texto, n }) => (
  <g>
    <rect x={x} y={y} width={w} height="30" rx="15" fill="#FFFFFF" stroke={GRIS} strokeWidth="1.5" />
    <circle cx={x + 16} cy={y + 15} r="18" fill={MARCA} className={`pab-toque-${n} pab-centro`} />
    <circle cx={x + 16} cy={y + 15} r="7" fill="none" stroke="#B8C2D6" strokeWidth="1.5" />
    <g className={`pab-marca-${n} pab-centro`}>
      <rect x={x} y={y} width={w} height="30" rx="15" fill="none" stroke={MARCA} strokeWidth="2.5" />
      <circle cx={x + 16} cy={y + 15} r="9" fill={MARCA} />
    </g>
    <path
      d={`M${x + 11.5} ${y + 15.5} l3.2 3.2 l6.2 -6.6`}
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`pab-tilde-${n}`}
    />
    <text x={x + 32} y={y + 20} fontFamily={FUENTE} fontSize="14" fontWeight="700" fill={TINTA}>{texto}</text>
  </g>
);

const Boleta = () => (
  <svg className="pab-voto" viewBox="0 0 440 340" aria-hidden="true">
    <defs>
      <filter id="pab-sombra" x="-20%" y="-20%" width="140%" height="150%">
        <feGaussianBlur stdDeviation="12" />
      </filter>
      <linearGradient id="pab-cabeza" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor={MARINO} />
        <stop offset="1" stopColor={REAL} />
      </linearGradient>
      <linearGradient id="pab-plata-texto" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#FFFFFF" />
        <stop offset="0.45" stopColor="#DDE1E7" />
        <stop offset="0.62" stopColor="#9EA5B2" />
        <stop offset="1" stopColor="#EEF0F4" />
      </linearGradient>
      <clipPath id="pab-tarjeta">
        <rect x="20" y="18" width="380" height="296" rx="16" />
      </clipPath>
    </defs>

    {/* Marco de líneas con punta, como el de "Categorías a premiar" del afiche */}
    <path d="M60 8 H6 V120" fill="none" stroke="#FFFFFF" strokeOpacity="0.8" strokeWidth="1.3" className="pab-marco-1" />
    <circle cx="6" cy="120" r="3.4" fill="#FFFFFF" className="pab-punto-1 pab-centro" />
    <path d="M414 262 V328 H300" fill="none" stroke="#FFFFFF" strokeOpacity="0.8" strokeWidth="1.3" className="pab-marco-2" />
    <circle cx="414" cy="262" r="3.4" fill="#FFFFFF" className="pab-punto-2 pab-centro" />

    <g className="pab-boleta pab-centro">
      <rect x="34" y="40" width="352" height="284" rx="16" fill="#000814" opacity="0.6" filter="url(#pab-sombra)" />
      <rect x="20" y="18" width="380" height="296" rx="16" fill={PAPEL} />
      <g clipPath="url(#pab-tarjeta)">
        <rect x="20" y="18" width="380" height="52" fill="url(#pab-cabeza)" />
      </g>
      {/* Firma del premio, como en el afiche */}
      <text x="186" y="53" fontFamily={CONDENSADA} fontSize="23" fontWeight="600" fill="url(#pab-plata-texto)" letterSpacing="0.3" textAnchor="end">PREMIOS ANSA</text>
      <text x="193" y="43" fontFamily={CONDENSADA} fontSize="12" fontWeight="600" fill="url(#pab-plata-texto)">20</text>
      <text x="193" y="56" fontFamily={CONDENSADA} fontSize="12" fontWeight="600" fill="url(#pab-plata-texto)">26</text>
      <text x="380" y="49" fontFamily={FUENTE} fontSize="10.5" fontWeight="500" fill="#B9C3DC" textAnchor="end" letterSpacing="1.2">4TA EDICIÓN</text>

      {/* Detergente */}
      <text x="40" y="99" fontFamily={FUENTE} fontSize="13.5" fontWeight="700" fill={TINTA}>Detergente</text>
      <Opcion x={40} y={109} w={84} />
      <Opcion x={132} y={109} w={84} />
      <Elegida x={224} y={109} w={92} texto="Vale" n={1} />

      <line x1="40" y1="158" x2="380" y2="158" stroke={GRIS} strokeWidth="1.2" strokeDasharray="4 5" />

      {/* Papel higiénico: no está nominado, se escribe en Otro */}
      <text x="40" y="184" fontFamily={FUENTE} fontSize="13.5" fontWeight="700" fill={TINTA}>Papel higiénico</text>
      <Opcion x={40} y={194} w={84} />
      <Opcion x={132} y={194} w={84} />
      <Elegida x={224} y={194} w={92} texto="Otro" n={2} />

      <rect x="40" y="236" width="236" height="34" rx="8" fill="#FFFFFF" stroke={GRIS} strokeWidth="1.5" />
      <rect x="40" y="236" width="236" height="34" rx="8" fill="none" stroke={MARCA} strokeWidth="2" className="pab-campo" />
      <text x="54" y="258" fontFamily={FUENTE} fontSize="13" fill="#9AA6BD" className="pab-pista">Escribe la marca</text>
      <rect x="53" y="244" width="1.8" height="18" fill={TINTA} className="pab-cursor-1" />
      <text x="54" y="259" fontFamily={FUENTE} fontSize="15" fontWeight="700" fill={TINTA}>
        <tspan className="pab-l1">T</tspan>
        <tspan className="pab-l2">i</tspan>
        <tspan className="pab-l3">s</tspan>
        <tspan className="pab-l4">u</tspan>
        <tspan className="pab-l5">f</tspan>
      </text>
      <g className="pab-cursor-2">
        <rect x="98" y="244" width="1.8" height="18" fill={TINTA} className="pab-parpadeo" />
      </g>
    </g>

    {/* Las marcas salen de la boleta como calcomanías */}
    <g transform="rotate(-8 372 116)">
      <image href={logoVale} x="320" y="82" width="104" height="66" className="pab-vale pab-centro" />
    </g>
    <g transform="rotate(7 362 224)">
      <image href={logoTisuf} x="314" y="192" width="96" height="66" className="pab-tisuf pab-centro" />
    </g>

    {/* Sello */}
    <g transform="rotate(-12 356 288)">
      <g className="pab-sello pab-centro">
        <circle cx="356" cy="288" r="32" fill={REAL} />
        <circle cx="356" cy="288" r="27" fill="none" stroke="#DDE3EE" strokeWidth="1.4" strokeDasharray="3 3" />
        <text x="356" y="284" fontFamily={FUENTE} fontSize="11" fontWeight="700" fill="#DDE3EE" textAnchor="middle">Voto</text>
        <text x="356" y="300" fontFamily={FUENTE} fontSize="14.5" fontWeight="700" fill="#FFFFFF" textAnchor="middle">SURA</text>
      </g>
    </g>
  </svg>
);

const PremiosAnsaBanner = () => {
  const [enFecha] = useState(enFechas);

  // intro (primera vuelta completa) -> bucle (solo la boleta, infinito).
  // Con movimiento reducido se queda en el estado final, quieto.
  const [estado, setEstado] = useState(() => (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'final' : 'intro'
  ));
  const [enVista, setEnVista] = useState(false);
  const [paginaVisible, setPaginaVisible] = useState(() => document.visibilityState === 'visible');
  const raizRef = useRef(null);

  // Corre mientras se vea al menos el 40% del banner y la pestaña esté visible
  useEffect(() => {
    if (!enFecha || !raizRef.current) return undefined;
    const observador = new IntersectionObserver(([entrada]) => setEnVista(entrada.isIntersecting), { threshold: 0.4 });
    observador.observe(raizRef.current);
    const alCambiarVisibilidad = () => setPaginaVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      observador.disconnect();
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [enFecha]);

  if (!enFecha) return null;

  const pasarAlBucle = (e) => {
    if (e.animationName === 'pab-reloj-k') setEstado('bucle');
  };

  const pausado = !enVista || !paginaVisible;

  return (
    <div className="pab-contenedor">
      <style>{styles}</style>
      <a
        href={ENLACE_VOTO}
        target="_blank"
        rel="noopener noreferrer"
        className="pab-enlace"
        aria-label="Votar por los productos SURA en los Premios ANSA 2026: Vale en Detergente y, en Papel Higiénico, Otro con Tisuf (se abre en otra pestaña)"
      >
        <section
          ref={raizRef}
          className="pab"
          data-estado={estado}
          data-pausado={pausado ? 'true' : 'false'}
          aria-labelledby="pab-titulo"
        >
          <Fondo />

          <div className="pab-texto">
            <div className="pab-firma">
              <img src={logoSura} alt="SURA" className="pab-logo-sura" />
              <span className="pab-separador" aria-hidden="true" />
              <img src={logoAnsa} alt="ANSA" className="pab-logo-ansa" />
            </div>
            <h2 id="pab-titulo" className="pab-titulo" aria-label="¡Vota por los productos SURA!">
              <span className="pab-titulo-1" aria-hidden="true"><Plata texto="¡Vota por los" /></span>
              <span className="pab-titulo-2" aria-hidden="true"><Plata texto="productos SURA!" desde={13} /></span>
            </h2>
            <p className="pab-bajada">
              El consumidor elige la Marca Preferida del Año. Vota por Vale en Detergente, y en Papel Higiénico marca «Otro» y escribe Tisuf.
            </p>
            <div className="pab-cta">
              <span className="pab-boton">Vota ahora</span>
              <span className="pab-tiempo">Toma menos de un minuto</span>
            </div>
          </div>

          <Boleta />

          <span className="pab-reloj" aria-hidden="true" onAnimationEnd={pasarAlBucle} />
        </section>
      </a>
    </div>
  );
};

export default PremiosAnsaBanner;
